import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, CalendarCheck, WheatOff, Flame, Star } from "lucide-react";
import { PhoneFrame } from "./PhoneFrame";
import { RESTAURANT, LANGS, UI, ALLERGENS, CATEGORIES, DISHES } from "@/demo/menuData";

const FILTERS = [
  { id: "gf", labelKey: "glutenFree", test: (d) => !d.allergens.includes(1) },
  { id: "noShell", labelKey: "noShellfish", test: (d) => !d.allergens.includes(2) },
  { id: "noDairy", labelKey: "noDairy", test: (d) => !d.allergens.includes(7) },
];

function Dish({ d, lang, t, index }) {
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.35, delay: index * 0.03 }}
      data-testid={`menu-dish-${d.id}`}
      className="flex gap-3 border-b border-line py-3.5 last:border-0"
    >
      <img src={d.img} alt="" loading="lazy" className="h-[72px] w-[72px] flex-none rounded-xl object-cover" />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-serif text-[15px] leading-tight text-bone">{d.name[lang]}</h4>
          <span className="flex-none font-accent text-sm text-ochre">€{d.price}</span>
        </div>
        <p className="mt-1 text-[11.5px] leading-snug text-stone">{d.desc[lang]}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {d.tags.includes("chef") && (
            <span className="inline-flex items-center gap-1 rounded-full bg-terracotta/15 px-2 py-0.5 text-[9px] uppercase tracking-wider text-terracotta">
              <Star size={9} /> {t.chef}
            </span>
          )}
          {d.tags.includes("classic") && (
            <span className="inline-flex items-center gap-1 rounded-full bg-ochre/15 px-2 py-0.5 text-[9px] uppercase tracking-wider text-ochre">
              <Flame size={9} /> {t.classic}
            </span>
          )}
          {d.tags.includes("gf") && (
            <span className="inline-flex items-center gap-1 rounded-full bg-cypress/30 px-2 py-0.5 text-[9px] uppercase tracking-wider text-[#b8d4bd]">
              <WheatOff size={9} /> {t.glutenFree}
            </span>
          )}
          {d.allergens.map((a) => (
            <span
              key={a}
              title={ALLERGENS[a][lang]}
              className="flex h-4 min-w-4 items-center justify-center rounded border border-line px-1 text-[9px] text-faded"
            >
              {a}
            </span>
          ))}
        </div>
      </div>
    </motion.article>
  );
}

export default function MenuDemo({ compact = false }) {
  const [lang, setLang] = useState("it");
  const [cat, setCat] = useState("antipasti");
  const [active, setActive] = useState([]);
  const t = UI[lang];

  const toggle = (id) => setActive((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  const dishes = useMemo(
    () => DISHES.filter((d) => d.cat === cat && FILTERS.filter((f) => active.includes(f.id)).every((f) => f.test(d))),
    [cat, active]
  );

  return (
    <PhoneFrame testid="menu-demo">
      <div className="flex h-full flex-col">
        <div className="relative flex-none px-5 pb-4 pt-12">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-accent text-[9px] uppercase tracking-[0.3em] text-terracotta">{RESTAURANT.tagline[lang]}</p>
              <h3 className="mt-1 font-serif text-2xl tracking-tight text-bone">{RESTAURANT.name}</h3>
              <p className="mt-1 text-[10.5px] text-faded">{t.open}</p>
            </div>
            <div className="flex rounded-full border border-line bg-panel p-0.5" role="tablist" aria-label="Lingua">
              {LANGS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => setLang(l.id)}
                  data-testid={`menu-lang-${l.id}`}
                  aria-pressed={lang === l.id}
                  className={`rounded-full px-2.5 py-1 font-accent text-[10px] font-semibold tracking-wider transition-colors duration-300 ${
                    lang === l.id ? "bg-terracotta text-[#1c0d08]" : "text-stone hover:text-bone"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div className="scrollbar-none -mx-5 mt-4 flex gap-2 overflow-x-auto px-5">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                data-testid={`menu-cat-${c.id}`}
                className={`flex-none rounded-full border px-3.5 py-1.5 text-[11px] transition-colors duration-300 ${
                  cat === c.id ? "border-bone bg-bone text-ink" : "border-line text-stone hover:border-line-strong"
                }`}
              >
                {c[lang]}
              </button>
            ))}
          </div>

          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => toggle(f.id)}
                data-testid={`menu-filter-${f.id}`}
                aria-pressed={active.includes(f.id)}
                className={`rounded-md border px-2 py-1 text-[10px] transition-colors duration-300 ${
                  active.includes(f.id) ? "border-ochre/60 bg-ochre/15 text-ochre" : "border-line text-faded hover:text-stone"
                }`}
              >
                {t[f.labelKey]}
              </button>
            ))}
          </div>
        </div>

        <div className="scrollbar-none flex-1 overflow-y-auto px-5" data-testid="menu-dish-list">
          <AnimatePresence mode="popLayout">
            {dishes.map((d, i) => (
              <Dish key={d.id} d={d} lang={lang} t={t} index={i} />
            ))}
          </AnimatePresence>
          {dishes.length === 0 && (
            <p data-testid="menu-empty" className="py-10 text-center text-sm text-faded">
              {t.empty}
            </p>
          )}
          <p className="pb-4 pt-3 text-center text-[10px] text-faded">{t.prices}</p>
        </div>

        <div className="flex flex-none gap-2 border-t border-line bg-panel px-4 py-3">
          <a
            href={`tel:${RESTAURANT.phone.replace(/\s/g, "")}`}
            data-testid="menu-call-button"
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-line py-2.5 text-xs text-bone transition-colors duration-300 hover:border-bone/50"
          >
            <Phone size={13} /> {t.call}
          </a>
          <a
            href={compact ? "/demo" : "#prenota"}
            data-testid="menu-book-button"
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-terracotta py-2.5 font-accent text-[11px] font-semibold uppercase tracking-wider text-[#1c0d08] transition-colors duration-300 hover:bg-[#f0695a]"
          >
            <CalendarCheck size={13} /> {t.book}
          </a>
        </div>
      </div>
    </PhoneFrame>
  );
}
